import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Gift, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

const FALLBACK = { currency: "usd", min: 100, max: 50000, presets: [200, 500, 1000, 2000] };
const dollars = (cents) => `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;

// Flag-gated shell (REACT_APP_MICROTX). Sends a tip to the DM peer via Stripe Checkout.
// Backend endpoints are PENDING — a 404/pending is surfaced as a friendly "coming soon" toast.
export function TipDialog({ open, onOpenChange, recipient, context }) {
  const { data } = useQuery({ queryKey: ["tips-config"], queryFn: api.tipsConfig, enabled: open, retry: false });
  const cfg = { ...FALLBACK, ...(data || {}) };
  const [amount, setAmount] = useState(cfg.presets?.[1] || 500);
  const [custom, setCustom] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const effective = custom ? Math.round(Number(custom) * 100) : amount;
  const valid = effective >= (cfg.min || 100) && effective <= (cfg.max || 50000);

  const send = async () => {
    if (!valid) return toast.error(`Enter an amount between ${dollars(cfg.min)} and ${dollars(cfg.max)}.`);
    setBusy(true);
    try {
      const res = await api.tipCheckout({
        recipient_id: recipient?.id,
        amount: effective,
        context,
        message: message.trim() || undefined,
        origin_url: `${window.location.origin}/app/chats`,
      });
      if (res?.checkout_url) {
        if (res.session_id) {
          localStorage.setItem(
            "bloq_pending_tip",
            JSON.stringify({
              session_id: res.session_id,
              amount: effective,
              handle: recipient?.handle || recipient?.username,
              message: message.trim() || undefined,
            }),
          );
        }
        window.location.href = res.checkout_url;
        return;
      }
      toast.error("Couldn't start the tip checkout.");
    } catch (e) {
      const st = e?.response?.status;
      if (st === 404 || st === 405) toast.info("Tips are coming soon — this feature isn't live yet.");
      else if (st === 409) toast.error("This person can't receive tips yet (payouts not set up).");
      else if (st === 403) toast.info("Tips are available in the mobile app.");
      else toast.error(e?.response?.data?.detail || "Couldn't send the tip.");
    } finally {
      setBusy(false);
    }
  };

  const name = recipient?.display_name || (recipient?.handle ? `@${recipient.handle}` : "them");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-bloq-s1 text-white sm:max-w-sm" data-testid="tip-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Gift className="h-5 w-5 text-bloq-mint" /> Send a tip
          </DialogTitle>
          <DialogDescription className="text-sm text-bloq-muted">
            Show {name} some love. Tips go straight to their wallet via secure checkout.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-2" data-testid="tip-presets">
            {(cfg.presets || FALLBACK.presets).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => { setAmount(p); setCustom(""); }}
                className={cn(
                  "rounded-xl border py-2.5 text-sm font-bold transition-colors",
                  !custom && amount === p ? "border-bloq-mint/60 bg-bloq-mint/15 text-bloq-mint" : "border-white/10 text-bloq-muted hover:text-white",
                )}
                data-testid={`tip-preset-${p}`}
              >
                {dollars(p)}
              </button>
            ))}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-bloq-muted">Custom amount</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-bloq-muted">$</span>
              <Input
                type="number" min={(cfg.min || 100) / 100} step="0.01"
                value={custom} onChange={(e) => setCustom(e.target.value)}
                placeholder="Other" className="h-11 rounded-xl border-white/12 bg-bloq-bg pl-7"
                data-testid="tip-custom-input"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-bloq-muted">Message (optional)</Label>
            <Input value={message} onChange={(e) => setMessage(e.target.value)} maxLength={140} placeholder="Say thanks…" className="h-11 rounded-xl border-white/12 bg-bloq-bg" data-testid="tip-message-input" />
          </div>
          <Button onClick={send} disabled={busy || !valid} className="h-12 w-full rounded-full bloq-gradient font-bold text-bloq-bg hover:opacity-90 disabled:opacity-50" data-testid="tip-send-btn">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : `Send ${dollars(effective)}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
